import {
  Asset,
  Onboarding,
  OnboardingPage,
  OnboardingQuestion,
  OnboardingQuestionType,
} from '../generated/gql-types';

export const WORK_ELIGIBILITY_PAGE_KEY = 'workEligibility';
export const WORK_QUALIFICATIONS_PAGE_KEY = 'workQualifications';

export type DocumentDateFieldKey = 'issueDate' | 'expiryDate';

export type DocumentDateRequirement = {
  required: boolean;
  title: string;
};

export type DocumentSlotDateRequirements = Partial<
  Record<DocumentDateFieldKey, DocumentDateRequirement>
>;

export type DocumentSlot = {
  tag: string;
  title: string;
  required: boolean;
  dateRequirements: DocumentSlotDateRequirements;
};

const FILE_QUESTION_TYPES = new Set([
  OnboardingQuestionType.FileUpload,
  OnboardingQuestionType.PhotoUpload,
]);

function extractDateRequirements(
  question: OnboardingQuestion,
): DocumentSlotDateRequirements {
  const requirements: DocumentSlotDateRequirements = {};

  for (const child of question.childQuestions ?? []) {
    if (child.type !== OnboardingQuestionType.Date) {
      continue;
    }
    if (child.key === 'issueDate' || child.key === 'expiryDate') {
      requirements[child.key] = {
        required: child.required,
        title: child.title,
      };
    }
  }

  return requirements;
}

function questionToSlot(question: OnboardingQuestion): DocumentSlot | null {
  if (!FILE_QUESTION_TYPES.has(question.type)) {
    return null;
  }

  return {
    tag: question.key,
    title: question.title,
    required: question.required,
    dateRequirements: extractDateRequirements(question),
  };
}

function collectSlotsFromQuestions(
  questions: OnboardingQuestion[] | null | undefined,
): DocumentSlot[] {
  const slots: DocumentSlot[] = [];
  const seen = new Set<string>();

  const walk = (question: OnboardingQuestion) => {
    const slot = questionToSlot(question);
    if (slot && !seen.has(slot.tag)) {
      seen.add(slot.tag);
      slots.push(slot);
    }
    question.childQuestions?.forEach(walk);
  };

  questions?.forEach(walk);
  return slots;
}

function getPageSlots(
  onboarding: Onboarding | null | undefined,
  pageKey: string,
): DocumentSlot[] {
  const page = onboarding?.onboardingJSON?.pages?.find(
    (p: OnboardingPage) => p.key === pageKey,
  );
  return collectSlotsFromQuestions(page?.questions ?? undefined);
}

export function getWorkEligibilityDocumentSlots(
  onboarding: Onboarding | null | undefined,
): DocumentSlot[] {
  return getPageSlots(onboarding, WORK_ELIGIBILITY_PAGE_KEY);
}

export function getWorkQualificationDocumentSlots(
  onboarding: Onboarding | null | undefined,
): DocumentSlot[] {
  return getPageSlots(onboarding, WORK_QUALIFICATIONS_PAGE_KEY);
}

export function getAllRequiredDocumentTags(
  eligibilitySlots: DocumentSlot[],
  qualificationSlots: DocumentSlot[],
): Set<string> {
  return new Set(
    [...eligibilitySlots, ...qualificationSlots].map((slot) => slot.tag),
  );
}

export function getOrphanAssets(
  assets: Asset[],
  requiredTags: Set<string>,
): Asset[] {
  return assets.filter((asset) => asset.tag && !requiredTags.has(asset.tag));
}
